import React, { useMemo } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { get } from 'lodash';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import { formatCsvFileName, formatDateTime } from '../helpers/formatters';
import { getUserFullName } from '../helpers/user';
import routes from '../routes';
import { sortByDateAndTime } from '../helpers/sorters';
import { rangeFilter } from '../helpers/MuiTableCustomFilters';
import { FF_USER_GROUPS } from './FeatureFlagWrapper';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';

const PushTable = ({ tableId, isLoading, pushes = [] }) => {
  const hasUserGroupsFF = useHasActiveFeatureFlag(FF_USER_GROUPS);
  const pushSentByList = useMemo(
    () =>
      Object.values(
        pushes.reduce((acc, push) => {
          acc[push.created_by.id] = push.created_by;
          return acc;
        }, {})
      ),
    [pushes.length]
  );

  const createdByCustomBodyRender = (value, csv = false) => {
    const userFullName = getUserFullName(value);

    if (csv) {
      return userFullName;
    }

    return <Link to={generatePath(routes.userEditNew, { id: value.id })}>{userFullName}</Link>;
  };

  const createdAtCustomBodyRender = (value) => formatDateTime(value.value, true);

  const renderSentByList = (filterList, onChange, index, column) => (
    <FormControl variant="standard">
      <InputLabel htmlFor="sent_by-selector">{column.label}</InputLabel>
      <Select
        variant="standard"
        id="sent_by-selector"
        multiple
        value={filterList[index]}
        renderValue={(selected) => selected.join(', ')}
        onChange={(event) => {
          filterList[index] = event.target.value;
          onChange(filterList[index], index, column);
        }}
      >
        {pushSentByList.map((user) => (
          <MenuItem key={user.id} value={getUserFullName(user)}>
            <Checkbox color="primary" checked={filterList[index].indexOf(getUserFullName(user)) > -1} />
            <ListItemText primary={getUserFullName(user)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <BaseMUIDataTable
      isLoading={isLoading}
      tableId={tableId}
      data={pushes}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            sort: false,
            display: false,
          },
        },
        {
          name: 'title',
          label: 'Title',
          options: {
            filter: false,
          },
        },
        {
          name: 'body',
          label: 'Body',
          options: {
            filter: false,
          },
        },
        {
          name: 'number_of_recipients',
          label: '# of Receivers',
          options: {
            customFilterListOptions: {
              render: (value) => `# of Receivers: ${value}`,
            },
            filterOptions: {
              names: rangeFilter.names,
              logic: (claimsNo, filters) => rangeFilter.logic(claimsNo, get(filters, [0], '')),
            },
          },
        },
        ...(hasUserGroupsFF
          ? [
              {
                name: 'group',
                label: 'Group',
                options: {
                  display: true,
                  filter: false,
                  customBodyRender: (value) => (value ? value.name : '-'),
                  customBodyRenderCSV: (value) => (value ? value.name : '-'),
                },
              },
            ]
          : []),
        {
          name: 'created_by',
          label: 'Sent By',
          options: {
            filterType: 'custom',
            customFilterListOptions: {
              render: (value) => `Sent By: ${value}`,
            },
            filterOptions: {
              logic: (user, filters) => {
                if (!filters || filters.length === 0) {
                  return false;
                }

                if (typeof user === 'object') {
                  return !filters.includes(getUserFullName(user));
                }

                return true;
              },
              display: renderSentByList,
            },
            customBodyRender: (value) => createdByCustomBodyRender(value),
            customBodyRenderCSV: (value) => createdByCustomBodyRender(value, true),
          },
        },
        {
          name: 'created_at',
          label: 'Sent At',
          options: {
            sortCompare: (order) => (row1, row2) => sortByDateAndTime(order, row1.data.value, row2.data.value),
            customFilterListOptions: {
              render: (value) => `Sent At: ${value}`,
            },
            customBodyRender: (value) => createdAtCustomBodyRender(value),
            customBodyRenderCSV: (value) => createdAtCustomBodyRender(value),
          },
        },
      ]}
      options={{
        sortOrder: {
          name: 'id',
          direction: 'desc',
        },
        rowsPerPage: 15,
        selectableRows: 'none',
        responsive: 'simple',
        downloadOptions: {
          filename: formatCsvFileName('Sent Push'),
        },
      }}
    />
  );
};

export default PushTable;
